import React, { useCallback, useRef } from 'react';

type Options<T extends Function> = {
  /**
   * If `true`, the callback will be invoked immediately on the leading edge of
   * the timeout.
   */
  leading?: boolean;

  /**
   * Function to be called on every call, regardless of debounce. This is useful
   * for event handlers where you want to prevent default behavior immediately.
   */
  onEveryCall?: T;

  /**
   * If `true`, the callback will be invoked on the trailing edge of the
   * timeout.
   */
  trailing?: boolean;
};

/**
 * Returns a debounced version of the provided callback that will only be
 * invoked after the specified delay has elapsed since the last time it was
 * invoked. Multiple calls to the debounced function within the delay period
 * will reset the delay, prolonging the wait time.
 *
 * @example
 * ```tsx
 * const handleClick = useDebouncedCallback<
 *   React.MouseEventHandler<HTMLElement>
 * >(
 *   (ev) => {
 *     console.log('Button clicked!', ev);
 *   },
 *   [],
 *   500,
 *   {
 *     leading: true,
 *     onEveryCall: ev => {
 *       ev.preventDefault();
 *       ev.stopPropagation();
 *     },
 *     trailing: false
 *   }
 * );
 */
export default function useDebouncedCallback<T extends Function>(
  callback: T,
  dependencies: React.DependencyList,
  delay: number,
  opts: Options<T> = {}
) {
  const debouncingRef = useRef(0);

  return useCallback(
    (...args: any[]) => {
      opts.onEveryCall?.(...args);

      const trailing =
        !opts.leading || debouncingRef.current > 0 ? opts.trailing : false;

      function callCallback() {
        debouncingRef.current = 0;
        if (trailing) {
          callback(...args);
        }
      }

      if (debouncingRef.current) {
        clearTimeout(debouncingRef.current);

        debouncingRef.current = window.setTimeout(callCallback, delay);

        return;
      }

      if (opts.leading) {
        callback(...args);
      }

      debouncingRef.current = window.setTimeout(callCallback, delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callback, delay, opts.leading, opts.trailing, ...dependencies]
  );
}
